import React, { useState } from 'react';

const Showcase = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const features = [
    {
      category: 'core',
      title: '🎯 Prediction Markets',
      path: '/',
      description: 'Create and bet on future events. The main app.',
      status: 'production'
    },
    {
      category: 'core',
      title: '📊 Active Markets',
      path: '/markets',
      description: 'Browse all active prediction markets with live odds.',
      status: 'production'
    },
    {
      category: 'auth',
      title: '🔐 OTP Login',
      path: '/otp-login',
      description: 'Secure email-based authentication with one-time passwords.',
      status: 'production'
    },
    {
      category: 'philosophy',
      title: '📖 Philosophy Blog',
      path: '/philosophy',
      description: 'Deep thoughts on coding, featuring "The Philosophy of Tiny Steps".',
      status: 'production'
    },
    {
      category: 'dev',
      title: '🔧 Bet Engineer View',
      path: '/bet-engineer',
      description: 'See EXACTLY what happens when you place a bet. Full transparency.',
      status: 'production'
    },
    {
      category: 'dev',
      title: '🏗️ System Architecture',
      path: '/architecture',
      description: 'Interactive view of the entire FutrBet system architecture.',
      status: 'production'
    },
    {
      category: 'dev',
      title: '💾 D1 Database Test Panel',
      path: '/d1-test',
      description: 'Test CloudFlare D1 database operations in real-time.',
      status: 'production'
    },
    {
      category: 'testing',
      title: '🧪 Test Dashboard',
      path: '/test-dashboard',
      description: 'Central hub for all testing tools and utilities.',
      status: 'production'
    },
    {
      category: 'testing',
      title: '⚡ Live Betting Flow',
      path: '/live-betting-fixed',
      description: 'Real-time betting flow visualization with live updates.',
      status: 'production'
    },
    {
      category: 'testing',
      title: '🔄 Working Betting Flow',
      path: '/working-flow',
      description: 'Step-by-step breakdown of the entire betting process.',
      status: 'production'
    },
    {
      category: 'auth',
      title: '🔒 Auth Dissection',
      path: '/auth',
      description: 'Deep dive into authentication system internals.',
      status: 'production'
    },
    {
      category: 'auth',
      title: '✨ Ultra Auth Experience',
      path: '/ultra-auth',
      description: 'Enhanced authentication flow with visual feedback.',
      status: 'production'
    },
    {
      category: 'otp',
      title: '🔢 OTP Generator',
      path: '/otp',
      description: 'Generate one-time passwords for testing.',
      status: 'production'
    },
    {
      category: 'otp',
      title: '💾 OTP Storage',
      path: '/otp-storage',
      description: 'View and manage stored OTP codes.',
      status: 'production'
    },
    {
      category: 'otp',
      title: '✅ OTP Verification',
      path: '/otp-verify',
      description: 'Test OTP verification flow.',
      status: 'production'
    },
    {
      category: 'email',
      title: '📧 Email Sender',
      path: '/send-real-email',
      description: 'Send real emails via Resend API.',
      status: 'production'
    },
    {
      category: 'email',
      title: '📊 Email Status Tracker',
      path: '/email-tracker',
      description: 'Track email delivery status and metrics.',
      status: 'production'
    },
    {
      category: 'email',
      title: '🎨 Email Formatter',
      path: '/otp-format',
      description: 'Preview and format OTP email templates.',
      status: 'production'
    },
    {
      category: 'dev',
      title: '🤖 Claude Agent Builder',
      path: '/agent-builder',
      description: 'Build and test Claude AI agents.',
      status: 'beta'
    },
    {
      category: 'dev',
      title: '📈 System Visualizer',
      path: '/system',
      description: 'Real-time system metrics and performance.',
      status: 'production'
    },
    {
      category: 'admin',
      title: '👨‍💼 Admin Dashboard',
      path: '/admin',
      description: 'Admin panel for managing markets and users.',
      status: 'beta'
    },
    {
      category: 'misc',
      title: '📋 Changelog',
      path: '/changelog',
      description: 'Complete history of all changes and updates.',
      status: 'production'
    },
    {
      category: 'misc',
      title: '📚 This Showcase',
      path: '/showcase',
      description: 'You are here! All our features in one place.',
      status: 'production'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Features', emoji: '🌟' },
    { id: 'core', label: 'Core App', emoji: '🎯' },
    { id: 'auth', label: 'Authentication', emoji: '🔐' },
    { id: 'otp', label: 'OTP System', emoji: '🔢' },
    { id: 'email', label: 'Email System', emoji: '📧' },
    { id: 'dev', label: 'Developer Tools', emoji: '🔧' },
    { id: 'testing', label: 'Testing', emoji: '🧪' },
    { id: 'philosophy', label: 'Philosophy', emoji: '📖' },
    { id: 'admin', label: 'Admin', emoji: '👨‍💼' },
    { id: 'misc', label: 'Miscellaneous', emoji: '📦' }
  ];

  const filteredFeatures = selectedCategory === 'all'
    ? features
    : features.filter(f => f.category === selectedCategory);

  const stats = {
    total: features.length,
    production: features.filter(f => f.status === 'production').length,
    beta: features.filter(f => f.status === 'beta').length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <div className="bg-black bg-opacity-50 border-b border-green-400">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-green-400 mb-2">FutrBet Showcase</h1>
              <p className="text-gray-400">Every feature we've built, all in one place</p>
            </div>
            <a
              href="/"
              className="px-6 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors"
            >
              Back to App →
            </a>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-gray-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex gap-8 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Total Features:</span>
              <span className="text-green-400 font-bold text-lg">{stats.total}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Production:</span>
              <span className="text-green-400 font-bold">{stats.production}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Beta:</span>
              <span className="text-yellow-400 font-bold">{stats.beta}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Built in:</span>
              <span className="text-purple-400 font-bold">One Epic Night</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                selectedCategory === cat.id
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {cat.emoji} {cat.label}
            </button>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFeatures.map((feature, index) => (
            <a
              key={index}
              href={feature.path}
              className="group bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-green-400 hover:bg-gray-750 transition-all hover:transform hover:scale-105"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-xl font-bold text-white group-hover:text-green-400 transition-colors">
                  {feature.title}
                </h3>
                <span className={`px-2 py-1 rounded text-xs font-bold ${
                  feature.status === 'production'
                    ? 'bg-green-900 text-green-300'
                    : 'bg-yellow-900 text-yellow-300'
                }`}>
                  {feature.status}
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                {feature.description}
              </p>
              <div className="flex items-center justify-between">
                <code className="text-xs text-gray-500 bg-gray-900 px-2 py-1 rounded">
                  {feature.path}
                </code>
                <span className="text-green-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  Visit →
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 text-center text-gray-500 pb-8">
          <p className="mb-2">
            Built with ☕ and Claude Code in one incredible coding session
          </p>
          <p className="text-sm">
            {stats.total} features • {stats.production} in production • Countless lessons learned
          </p>
          <div className="mt-4">
            <a
              href="/philosophy"
              className="text-green-400 hover:text-green-300 underline"
            >
              Read: The Philosophy of Tiny Steps →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Showcase;